/**
 * The field app's Today split and the pinned card on top of it.
 *
 * Lifted out of the page (#1330) so it can run over TWO lists: an acting
 * helper's Today is the lead's day while his Upcoming is his own.
 *
 * #1609 (closeout dossier D9): the pinned card used to say "Next Job" over
 * whichever job ranked highest by STATUS, so a 4:45 PM job the tech had
 * tapped On My Way for sat above a 3:15 PM one and read as a mistake. The
 * rule now: the list is clock order, always. A job the tech has already
 * started (on site / working / driving) is pinned on top and SAYS so
 * ("Driving there now", "On site now"); only when nothing is started does the
 * card say "Next job", and then it is the earliest one on the clock.
 */
export interface DayJob {
    id: string;
    status: string;
    scheduled_start: string | null;
}

/** Started work: pinned above the clock-ordered list. On site beats driving. */
const STARTED_RANK: Record<string, number> = { in_progress: 0, arrived: 0, en_route: 1 };

function startMs(j: DayJob, missing: number): number {
    if (!j.scheduled_start) return missing;
    const t = new Date(j.scheduled_start).getTime();
    return Number.isFinite(t) ? t : missing;
}

export function splitDay<T extends DayJob>(
    jobs: T[],
    now: Date,
): { todayJobs: T[]; upcomingJobs: T[]; nextJob: T | null } {
    const all = jobs
        .slice()
        .sort((a, b) => startMs(a, Number.POSITIVE_INFINITY) - startMs(b, Number.POSITIVE_INFINITY));
    const todayList: T[] = [];
    const upcomingList: T[] = [];
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    for (const job of all) {
        const start = job.scheduled_start ? new Date(job.scheduled_start) : null;
        if (start && start >= tomorrow) {
            upcomingList.push(job);
        } else {
            // Today or past (not yet completed) — show in Today
            todayList.push(job);
        }
    }
    // Pinned card: a started job if there is one (on site before driving),
    // else the earliest job on the clock (todayList is already clock order).
    const started = todayList
        .filter((j) => j.status in STARTED_RANK)
        .sort((a, b) => STARTED_RANK[a.status] - STARTED_RANK[b.status] || startMs(a, 0) - startMs(b, 0));
    const next = started[0] ?? todayList[0] ?? upcomingList[0] ?? null;
    return { todayJobs: todayList, upcomingJobs: upcomingList, nextJob: next };
}

/** The words on the pinned card's header: what the tech is doing with it. */
export function nextJobHeading(status: string | null | undefined): string {
    switch (status) {
        case "en_route":
            return "Driving there now";
        case "arrived":
        case "in_progress":
            return "On site now";
        default:
            return "Next job";
    }
}
